<template>
  <v-card outlined :loading="loading">
    <v-card-title class="d-flex align-center">
      <span>{{ mode === "new" ? "New Auctioneer Record" : "Auctioneer Record" }}</span>

      <v-spacer />

      <v-chip small outlined color="primary">
        {{ localItem.type === "company" ? "Company" : "Auctioneer" }}
      </v-chip>
    </v-card-title>

    <v-divider />

    <v-card-text>
      <v-form ref="form" :disabled="loading">
        <v-row dense>
          <!-- record type -->
          <v-col cols="12" md="6">
            <v-select
              v-model="localItem.type"
              :items="typeOptions"
              item-text="text"
              item-value="value"
              label="Record Type"
              outlined
              dense
              :rules="[requiredField]"
            />
          </v-col>

          <!-- active -->
          <v-col cols="12" md="6">
            <div class="d-flex align-center" style="height: 40px;">
              <v-switch
                v-model="localItem.active"
                label="Active"
                color="primary"
                inset
                hide-details
                class="mt-0"
              />
            </div>
          </v-col>

          <!-- name -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model.trim="localItem.name"
              :label="
                localItem.type === 'company'
                  ? 'Auctioneer Company'
                  : 'Auctioneer Name'
              "
              outlined
              dense
              :rules="[requiredField]"
            />
          </v-col>

          <!-- companies -->
          <v-col
            v-if="localItem.type === 'auctioneer'"
            cols="12"
            md="6"
          >
            <v-autocomplete
              v-model="localItem.companyIds"
              :items="companyOptions"
              item-text="name"
              item-value="id"
              label="Auctioneer Companies"
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              outlined
              dense
              hint="An auctioneer may belong to more than one company."
              persistent-hint
            />
          </v-col>

          <!-- phone -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model.trim="localItem.phoneNumber"
              label="Phone Number"
              outlined
              dense
            />
          </v-col>

          <!-- sales rep -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model.trim="localItem.salesRep"
              label="Sales Rep"
              outlined
              dense
            />
          </v-col>

          <!-- contact email -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model.trim="localItem.contactEmail"
              label="Contact Email"
              type="email"
              outlined
              dense
            />
          </v-col>

          <!-- commission email -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model.trim="localItem.commissionEmail"
              label="Commission Email"
              type="email"
              outlined
              dense
            />
          </v-col>

          <!-- default commission -->
          <v-col cols="12">
            <v-sheet outlined rounded class="pa-4 mb-2">
              <div class="text-subtitle-2 font-weight-bold mb-1">
                Default Commission Rate
              </div>

              <div class="text-caption grey--text mb-4">
                This rate will be used as the default when the auctioneer or
                company is added to an Event Report. It can still be changed
                for an individual Event Report.
              </div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="commissionRateSelection"
                    :items="commissionRateOptions"
                    item-text="text"
                    item-value="value"
                    label="Default Commission Rate"
                    outlined
                    dense
                    clearable
                  />
                </v-col>

                <v-col
                  v-if="commissionRateSelection === 'other'"
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model.number="localItem.defaultCommissionRate"
                    label="Other Commission Rate"
                    type="number"
                    suffix="%"
                    min="0"
                    step="0.01"
                    outlined
                    dense
                    :rules="[validCommissionRate]"
                  />
                </v-col>
              </v-row>
            </v-sheet>
          </v-col>

          <!-- address -->
          <v-col cols="12">
            <v-text-field
              v-model.trim="localItem.mailingAddress"
              label="Mailing Address"
              outlined
              dense
            />
          </v-col>

          <!-- notes -->
          <v-col cols="12">
            <v-textarea
              v-model.trim="localItem.notes"
              label="Notes"
              outlined
              rows="5"
              auto-grow
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-divider />

    <v-card-actions>
      <v-btn
        text
        color="primary"
        :disabled="loading"
        @click="$emit('cancel')"
      >
        Cancel
      </v-btn>

      <v-spacer />

      <v-btn
        color="primary"
        depressed
        :loading="loading"
        :disabled="loading"
        @click="submit"
      >
        {{ mode === "new" ? "Create Record" : "Save Changes" }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: "AuctioneerDetails",

  props: {
    item: {
      type: Object,
      required: true,
    },

    companies: {
      type: Array,
      default: () => [],
    },

    loading: {
      type: Boolean,
      default: false,
    },

    mode: {
      type: String,
      default: "edit",
    },
  },

  data() {
    const localItem = this.buildLocalItem(this.item);

    return {
      localItem,
      commissionRateSelection: this.getCommissionRateSelection(
        localItem.defaultCommissionRate
      ),

      typeOptions: [
        {
          text: "Auctioneer",
          value: "auctioneer",
        },
        {
          text: "Auctioneer Company",
          value: "company",
        },
      ],

      commissionRateOptions: [
        { text: "5%", value: 5 },
        { text: "10%", value: 10 },
        { text: "15%", value: 15 },
        { text: "20%", value: 20 },
        { text: "25%", value: 25 },
        { text: "Other", value: "other" },
      ],
    };
  },

  computed: {
    companyOptions() {
      return this.companies
        .filter((item) => item && item.type === "company")
        .slice()
        .sort((a, b) => {
          return (a.name || "").localeCompare(b.name || "");
        });
    },
  },

  watch: {
    item: {
      deep: true,
      handler(value) {
        this.localItem = this.buildLocalItem(value);
        this.commissionRateSelection =
          this.getCommissionRateSelection(
            this.localItem.defaultCommissionRate
          );
      },
    },

    "localItem.type"(value) {
      if (value === "company") {
        this.localItem.companyIds = [];
      }
    },

    commissionRateSelection(value) {
      if (value === null || value === undefined || value === "") {
        this.localItem.defaultCommissionRate = null;
        return;
      }

      if (value !== "other") {
        this.localItem.defaultCommissionRate = Number(value);
      }
    },
  },

  methods: {
    buildLocalItem(item) {
      return {
        id: item.id || "",
        type: item.type || "auctioneer",

        active:
          item.active === undefined || item.active === null
            ? true
            : item.active,

        name: item.name || "",

        companyIds: Array.isArray(item.companyIds)
          ? [...item.companyIds]
          : [],

        phoneNumber: item.phoneNumber || "",
        contactEmail: item.contactEmail || "",
        commissionEmail: item.commissionEmail || "",
        defaultCommissionRate:
          item.defaultCommissionRate === undefined ||
          item.defaultCommissionRate === null ||
          item.defaultCommissionRate === ""
            ? null
            : Number(item.defaultCommissionRate),
        mailingAddress: item.mailingAddress || "",
        salesRep: item.salesRep || "",
        notes: item.notes || "",
      };
    },

    getCommissionRateSelection(rate) {
      if (rate === undefined || rate === null || rate === "") {
        return null;
      }

      const numericRate = Number(rate);
      const standardRates = [5, 10, 15, 20, 25];

      return standardRates.includes(numericRate)
        ? numericRate
        : "other";
    },

    requiredField(value) {
      if (value === undefined || value === null) {
        return "Field Required";
      }

      if (!String(value).replace(/\s/g, "")) {
        return "Field Required";
      }

      return true;
    },

    validCommissionRate(value) {
      if (this.commissionRateSelection !== "other") {
        return true;
      }

      if (
        value === undefined ||
        value === null ||
        value === ""
      ) {
        return "Commission rate is required";
      }

      if (Number(value) < 0) {
        return "Commission rate cannot be negative";
      }

      return true;
    },

    normalizeName(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
    },

    submit() {
      if (!this.$refs.form || !this.$refs.form.validate()) {
        return;
      }

      const payload = {
        ...this.localItem,

        name: this.localItem.name.trim(),

        nameNormalized: this.normalizeName(
          this.localItem.name
        ),

        defaultCommissionRate:
          this.localItem.defaultCommissionRate === null ||
          this.localItem.defaultCommissionRate === ""
            ? null
            : Number(this.localItem.defaultCommissionRate),

        companyIds:
          this.localItem.type === "auctioneer"
            ? [...this.localItem.companyIds]
            : [],
      };

      this.$emit("save", payload);
    },
  },
};
</script>